<div class="container-fluid p-0 profile-page">

    <!-- HEADER -->
    <HeaderSection 
        :breadcrumb="breadcrumb"
        @breadcrumb-click="handleBreadcrumbClick"
        @open-assistant="showAssistant = true"
    />

    <!-- LOADING STATE -->
    <div v-if="loading" class="container text-center py-5">
        <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
        <p class="text-white mt-2">Loading profile...</p>
    </div>

    <!-- ERROR STATE -->
    <div v-else-if="error" class="container py-5">
        <div class="alert alert-danger text-center">
            {{ error }}
        </div>
    </div>

    <!-- PROFILE CONTENT -->
    <div v-else>

        <!-- TOP SECTION -->
        <section class="container py-4">

            <!-- ROW: AVATAR + USER DETAILS + EDIT BUTTON -->
            <div class="row align-items-center">

                <!-- Avatar -->
                <div class="col-md-2 text-center text-md-start mb-3 mb-md-0">
                    <div class="profile-avatar d-flex align-items-center justify-content-center mx-auto mx-md-0">
                        <span class="profile-avatar-text">{{ avatar_initials }}</span>
                    </div>
                </div>

                <!-- User details -->
                <div class="col-md-7 text-center text-md-start">
                    <h3 class="fw-bold text-white mb-1">{{ fullName }}</h3>
                    <p class="text-white-50 mb-1">{{ userRole }} • Member since {{ joinYear }}</p>
                </div>

                <!-- Edit button -->
                <div class="col-md-3 text-center text-md-end mt-3 mt-md-0">
                    <router-link 
                        to="/edit_profile" 
                        class="btn btn-primary rounded-3 px-3 py-1 fw-light text-xs shadow-sm text-decoration-none"
                    >
                        <i class="bi bi-pencil-square me-1"></i> Edit Profile
                    </router-link>

                </div>
            </div>
        </section>

        <!-- CONTENT SECTIONS -->
        <section class="container profile-sections">

            <div class="row g-4">

                <!-- CONTACT INFORMATION -->
                <div class="col-md-6">
                    <div class="glass-card p-4 rounded-4 h-100">
                        <h6 class="text-white fw-semibold mb-3">Contact Information</h6>

                        <div class="d-flex align-items-center mb-3">
                            <div class="icon-box me-3">
                                <i class="bi bi-envelope-fill text-white"></i>
                            </div>
                            <span class="text-white-50">{{ userEmail }}</span>
                        </div>

                        <div class="d-flex align-items-center">
                            <div class="icon-box me-3">
                                <i class="bi bi-telephone-fill text-white"></i>
                            </div>
                            <span class="text-white-50">{{ userPhone }}</span>
                        </div>
                    </div>
                </div>

                <!-- PROFILE PROGRESS -->
                <div class="col-md-6">
                    <div class="glass-card p-4 rounded-4 h-100">
                        <div class="d-flex justify-content-between mb-2">
                            <h6 class="text-white fw-semibold mb-0">Profile Progress</h6>
                            <span class="text-white fw-bold fs-4">{{ profileProgress }}%</span>
                        </div>

                        <div class="progress rounded-3 mb-2 progress-lg">
                            <div 
                                class="progress-bar progress-bar-custom" 
                                :style="{ width: profileProgress + '%' }"
                            ></div>
                        </div>

                        <small class="text-white-50">
                            Complete <strong>{{ remainingItems }}</strong> more item{{ remainingItems !== 1 ? 's' : '' }} to reach 100%
                        </small>
                    </div>
                </div>

            </div>

            <!-- COMPLETE YOUR PROFILE -->
            <div class="glass-card p-4 rounded-4 mt-4">

                <h6 class="text-white fw-semibold mb-4">Complete Your Profile</h6>

                <div class="row g-3">

                    <!-- Email Verified -->
                    <div class="col-md-6">
                        <div class="completion-item p-3 rounded-3 d-flex align-items-center justify-content-between">
                            <div class="d-flex align-items-center">
                                <i 
                                    class="bi me-2" 
                                    :class="isEmailVerified ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"
                                ></i>
                                <span class="text-white-50">
                                    {{ isEmailVerified ? 'Email Verified' : 'Verify Email' }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Phone Number -->
                    <div class="col-md-6">
                        <div class="completion-item p-3 rounded-3 d-flex align-items-center justify-content-between" :class="{ 'incomplete': !hasPhoneNumber }">
                            <div class="d-flex align-items-center">
                                <i 
                                    class="bi me-2" 
                                    :class="hasPhoneNumber ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"
                                ></i>
                                <span class="text-white-50">
                                    {{ hasPhoneNumber ? 'Phone Added' : 'Add Phone Number' }}
                                </span>
                            </div>
                            <i v-if="!hasPhoneNumber" class="bi bi-chevron-right text-white-50"></i>
                        </div>
                    </div>

                    <!-- First Name -->
                    <div class="col-md-6">
                        <div class="completion-item p-3 rounded-3 d-flex align-items-center justify-content-between" :class="{ 'incomplete': !userProfile?.first_name }">
                            <div class="d-flex align-items-center">
                                <i 
                                    class="bi me-2" 
                                    :class="userProfile?.first_name ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"
                                ></i>
                                <span class="text-white-50">
                                    {{ userProfile?.first_name ? 'First Name Added' : 'Add First Name' }}
                                </span>
                            </div>
                            <i v-if="!userProfile?.first_name" class="bi bi-chevron-right text-white-50"></i>
                        </div>
                    </div>

                    <!-- Last Name -->
                    <div class="col-md-6">
                        <div class="completion-item p-3 rounded-3 d-flex align-items-center justify-content-between" :class="{ 'incomplete': !userProfile?.last_name }">
                            <div class="d-flex align-items-center">
                                <i 
                                    class="bi me-2" 
                                    :class="userProfile?.last_name ? 'bi-check-circle-fill text-success' : 'bi-x-circle-fill text-danger'"
                                ></i>
                                <span class="text-white-50">
                                    {{ userProfile?.last_name ? 'Last Name Added' : 'Add Last Name' }}
                                </span>
                            </div>
                            <i v-if="!userProfile?.last_name" class="bi bi-chevron-right text-white-50"></i>
                        </div>
                    </div>

                </div>

            </div>

        </section>
    </div>

    <!-- 🤖 VSI Assistant -->
    <BolideAssistant :is_open="showAssistant" @close-assistant="showAssistant = false" />

</div>